import type { LevelDef } from '../content/levelSchema';
import { needsMowing } from '../sim/grid';
import type { ItemId } from '../sim/items';
import type { PickupSpawn } from '../sim/level';
import { Rng } from '../sim/rng';

/**
 * How many item boxes a Daily of this difficulty (8 to 16) carries: 2, 3 or 4, more on the bigger
 * lawns (docs/DAILY_ITEMS.md).
 */
export function itemBoxCount(difficulty: number): number {
  return difficulty <= 10 ? 2 : difficulty <= 13 ? 3 : 4;
}

/**
 * The lawn with item boxes added: on grass still to mow, at least 3 tiles (walking) from the start,
 * 3 apart from each other (either way), and never on another pickup. The first two are one of each
 * kind; any more are either. From their own dice (`seed`), so the lawn itself is unchanged. Boxes
 * are never needed: the lawn was checked without them.
 */
export function withItemBoxes(def: LevelDef, seed: string, count: number): LevelDef {
  const rng = Rng.fromSeed(`boxes:${seed}`);
  const { sim } = def;
  const taken = new Set((sim.pickups ?? []).map((p) => p.y * sim.width + p.x));
  const spots: { x: number; y: number }[] = [];
  for (let y = 0; y < sim.height; y++) {
    for (let x = 0; x < sim.width; x++) {
      if (!needsMowing(sim.tiles[y * sim.width + x]!)) continue;
      if (Math.abs(x - sim.start.x) + Math.abs(y - sim.start.y) < 3) continue;
      if (taken.has(y * sim.width + x)) continue;
      spots.push({ x, y });
    }
  }
  rng.shuffle(spots);
  const kinds: ItemId[] = rng.shuffle<ItemId>(['turbo', 'trim']);
  while (kinds.length < count) kinds.push(rng.pick<ItemId>(['turbo', 'trim']));
  const boxes: PickupSpawn[] = [];
  for (const spot of spots) {
    if (boxes.length === count) break;
    if (boxes.some((b) => Math.max(Math.abs(b.x - spot.x), Math.abs(b.y - spot.y)) < 3)) continue;
    boxes.push({ kind: kinds[boxes.length]!, x: spot.x, y: spot.y });
  }
  return { ...def, sim: { ...sim, pickups: [...(sim.pickups ?? []), ...boxes] } };
}
